import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { ROLE_LABELS } from '../lib/status';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  LayoutDashboard, Building2, ClipboardList, Receipt, BarChart3, RefreshCw,
  Wifi, WifiOff, LogOut, Sun, Moon, Boxes, Menu, X, CloudUpload, Images, Megaphone, Users, ShieldCheck,
} from 'lucide-react';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'operator', 'client', 'society_manager', 'billing'] },
  { to: '/societies', label: 'Societies', icon: Building2, roles: ['admin', 'manager', 'billing', 'society_manager'] },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone, roles: ['admin', 'manager'] },
  { to: '/clients', label: 'Clients', icon: Users, roles: ['admin'] },
  { to: '/staff', label: 'User Master', icon: Users, roles: ['admin'] },
  { to: '/inspections', label: 'Inspections', icon: ClipboardList, roles: ['admin', 'manager', 'operator'] },
  { to: '/proofs', label: 'Proof Gallery', icon: Images, roles: ['admin', 'manager', 'client', 'society_manager'] },
  { to: '/billing', label: 'Billing', icon: Receipt, roles: ['admin', 'billing', 'society_manager'] },
  { to: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'manager', 'client', 'society_manager', 'billing'] },
  { to: '/audit', label: 'Data Audit', icon: ShieldCheck, roles: ['admin', 'manager'] },
  { to: '/sync', label: 'Sync Queue', icon: CloudUpload, roles: ['admin', 'manager', 'operator'] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { online, simOffline, setSimOffline, pendingCount, processQueue } = useSync();
  const navigate = useNavigate();
  const [dark, setDark] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);

  const links = NAV.filter((n) => n.roles.includes(user.role) || user.role === 'admin');

  const toggleTheme = () => {
    const html = document.documentElement;
    if (dark) { html.classList.remove('dark'); html.classList.add('light'); }
    else { html.classList.add('dark'); html.classList.remove('light'); }
    setDark(!dark);
  };

  const NavItems = ({ onClick }) => (
    <>
      {links.map((n) => (
        <NavLink
          key={n.to} to={n.to} end={n.to === '/'} onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, '-')}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
              isActive ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
        >
          <n.icon className="h-[18px] w-[18px]" />
          <span>{n.label}</span>
          {n.to === '/sync' && pendingCount > 0 && (
            <span className="ml-auto rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-mono text-sky-300">{pendingCount}</span>
          )}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen">
      {/* Offline banner */}
      {!online && (
        <div data-testid="offline-banner" className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-500 py-1.5 text-center text-xs font-semibold text-amber-950">
          <WifiOff className="h-3.5 w-3.5" /> Offline mode — {pendingCount} action(s) queued locally. Auto-sync when network returns.
        </div>
      )}

      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-card/50 p-4 lg:flex">
          <div className="mb-6 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Boxes className="h-5 w-5" /></div>
            <div>
              <p className="font-head text-lg font-extrabold leading-none">DIZITTAL</p>
              <p className="overline text-muted-foreground">Inspect</p>
            </div>
          </div>
          <nav className="flex flex-1 flex-col gap-1"><NavItems /></nav>
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <div className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><WifiOff className="h-3.5 w-3.5" /> Simulate Offline</span>
              <Switch data-testid="offline-simulator-toggle" checked={simOffline} onCheckedChange={setSimOffline} />
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 truncate">
                <p className="truncate text-sm font-medium">{user.name}</p>
                <p className="overline text-primary">{ROLE_LABELS[user.role]}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={toggleTheme} data-testid="theme-toggle">{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
              <Button size="icon" variant="ghost" data-testid="logout-btn" onClick={() => { logout(); navigate('/login'); }}><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-background/90 px-4 py-2.5 backdrop-blur lg:px-6">
            <button className="lg:hidden" onClick={() => setMobileNav(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
            <div className="flex items-center gap-2 lg:hidden">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground"><Boxes className="h-4 w-4" /></div>
              <span className="font-head text-base font-extrabold">DIZITTAL INSPECT</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span data-testid="network-status" className={`hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-mono sm:flex ${online ? 'border-emerald-600 text-emerald-400' : 'border-amber-600 text-amber-400'}`}>
                {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />} {online ? 'ONLINE' : 'OFFLINE'}
              </span>
              <Button size="sm" variant="secondary" data-testid="manual-sync-trigger" onClick={() => processQueue()} disabled={!online}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync All {pendingCount > 0 ? `(${pendingCount})` : ''}
              </Button>
            </div>
          </header>

          <main className="flex-1 p-4 pb-24 lg:p-6"><Outlet /></main>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 h-full w-72 rise bg-card p-4">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-head text-lg font-extrabold">DIZITTAL INSPECT</span>
              <button onClick={() => setMobileNav(false)}><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex flex-col gap-1"><NavItems onClick={() => setMobileNav(false)} /></nav>
            <div className="mt-4 flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2">
              <span className="text-xs text-muted-foreground">Simulate Offline</span>
              <Switch checked={simOffline} onCheckedChange={setSimOffline} />
            </div>
            <Button variant="ghost" className="mt-3 w-full justify-start" onClick={() => { logout(); navigate('/login'); }}><LogOut className="mr-2 h-4 w-4" /> Logout ({ROLE_LABELS[user.role]})</Button>
          </div>
        </div>
      )}
    </div>
  );
}
